import { config } from "Utils/config"

const remoteCache = new Map()
const inflight = new Map()
const localeMap = new Map()
const defaultManifest = {
  default: "en",
  locales: [
    { id: 0, locale: "en", label: "English", flag: "🇬🇧", aliases: ["0", "en", "en-us", "en_gb"] },
    { id: 1, locale: "fr", label: "Français", flag: "🇫🇷", aliases: ["1", "fr", "fr-fr", "fr_fr"] },
  ],
}

let manifestCache = null
let manifestInflight = null
let defaultLocale = defaultManifest.default || "en"

function applyLocales(locales) {
  localeMap.clear()
  locales.forEach(entry => {
    const locale = entry.locale?.toLowerCase()
    if (!locale) return
    localeMap.set(String(entry.id), locale)
    localeMap.set(locale, locale)
    entry.aliases?.forEach(alias => {
      localeMap.set(String(alias).toLowerCase(), locale)
    })
  })
}

applyLocales(defaultManifest.locales)

const apiBaseUrl = (() => {
  const url = config.APOLLO_SERVER_URL || config.API_URL || ""
  if (!url) return ""
  return url.replace(/\/graphql$/i, "")
})()

export function normalizeLocale(langageId) {
  if (langageId === null || langageId === undefined) return "en"
  if (typeof langageId === "object") return undefined
  const lower = String(langageId).trim().toLowerCase()
  return localeMap.get(lower) || lower || defaultLocale || "en"
}

function buildUrl(locale) {
  if (apiBaseUrl) {
    const sanitized = apiBaseUrl.endsWith("/") ? apiBaseUrl.slice(0, -1) : apiBaseUrl
    return `${sanitized}/translations/${locale}`
  }
  return `/translations/${locale}`
}

function buildManifestUrl() {
  if (apiBaseUrl) {
    const sanitized = apiBaseUrl.endsWith("/") ? apiBaseUrl.slice(0, -1) : apiBaseUrl
    return `${sanitized}/translations/manifest`
  }
  return "/translations/manifest"
}

export function getAvailableLocales() {
  return manifestCache?.locales?.length ? manifestCache.locales : defaultManifest.locales
}

export function ensureManifest() {
  if (manifestCache && !manifestInflight) {
    return Promise.resolve(manifestCache)
  }
  if (manifestInflight) return manifestInflight
  if (manifestInflight) return manifestInflight

  manifestInflight = fetch(buildManifestUrl(), { credentials: "omit" })
    .then(response => {
      if (!response.ok) throw new Error(`Failed to load manifest (${response.status})`)
      return response.json()
    })
    .then(data => {
      if (data && Array.isArray(data.locales) && data.locales.length) {
        manifestCache = data
        defaultLocale = data.default || data.locales[0]?.locale || defaultLocale
        applyLocales(data.locales)
        return manifestCache
      }
      return manifestCache || defaultManifest
    })
    .catch(error => {
      console.warn?.("[translations] Unable to load manifest", error)
      return manifestCache || defaultManifest
    })
    .finally(() => {
      manifestInflight = null
    })

  return manifestInflight
}

export function getCachedRemoteTranslation(langageId) {
  const locale = normalizeLocale(langageId)
  if (!locale) return undefined
  return remoteCache.get(locale)
}

export function getLocaleInfo(langageId) {
  const localeCode = normalizeLocale(langageId)
  const entry =
    manifestCache?.locales?.find(
      loc =>
        loc.locale?.toLowerCase() === localeCode?.toLowerCase() ||
        String(loc.id) === String(langageId)
    ) || manifestCache?.locales?.find(loc => loc.locale === localeCode)
  return entry || null
}

export function getLocaleSuffix(langageId) {
  const locale = normalizeLocale(langageId)
  if (!locale) return "EN"
  return locale.split("-")[0]?.toUpperCase() || locale.toUpperCase()
}

export async function ensureRemoteTranslation(langageId) {
  try {
    await ensureManifest()
  } catch (error) {
    // silencieux : on retombe sur les locales par défaut
  }
  const locale = normalizeLocale(langageId)
  if (!locale) return Promise.resolve(undefined)
  if (remoteCache.has(locale)) return Promise.resolve(remoteCache.get(locale))
  if (inflight.has(locale)) return inflight.get(locale)

  const url = buildUrl(locale)
  const request = fetch(url, { credentials: "omit" })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to load translations (${response.status})`)
      }
      return response.json()
    })
    .then(data => {
      if (data && typeof data === "object") {
        remoteCache.set(locale, data)
      }
      return data
    })
    .catch(error => {
      if (typeof console !== "undefined") {
        console.warn?.(`[translations] Unable to load locale ${locale}`, error)
      }
    })
    .finally(() => {
      inflight.delete(locale)
    })

  inflight.set(locale, request)
  return request
}


